import { Injectable } from '@angular/core';
import * as waxjs from '@waxio/waxjs/dist';
import { Api, JsonRpc } from 'eosjs';
import {concat, empty, EMPTY, from, interval, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {expand, filter, flatMap, map, reduce, repeatWhen, retry, startWith, takeLast, tap, toArray} from 'rxjs/operators';
import ScatterJS from 'scatterjs-core';
import ScatterEOS from 'scatterjs-plugin-eosjs2';
import AnchorLink from 'anchor-link';
import AnchorLinkBrowserTransport from 'anchor-link-browser-transport';

export interface TableRowUser {
  code: 'eosio';
  scope: string;
  table: 'userres';
  payer: string;
  count: number;
}
export interface TableRowsResult<T> {
  rows: T[];
  more: boolean;
}

export interface TableRowDrop {
  drop_id: number;
  collection_name: string;
  assets_to_mint: {
    template_id: number;
  }[];
  listing_price: string;
  settlement_symbol: string;
  price_recipient: string;
  fee_rate: number;
  auth_required: number;
  account_limit: number;
  account_limit_cooldown: number;
  max_claimable: number;
  current_claimed: number;
  start_time: number;
  end_time: number;
  display_data: string;
}

export interface TableRowPrice {
  id: number;
  median: number;
  owner: string;
  timestamp: string;
  value: number;
}
export interface TableRowPending {
  assoc_id: number;
  box_id: number;
  collection_name: string;
  unboxer: string;
  status: number;
  item_ids: number[];
}

export interface WaxAccount {
  account_name: string;
  head_block_num: number;
  head_block_time: string;
  last_code_update: string;
  created: string;
  refund_request: RefundRequest;
  ram_quota: string | number;
  net_limit: ResourceLimit;
  total_resources: ResourceOverview;
  core_liquid_balance: string;
  self_delegated_bandwidth: ResourceDelegation;
  net_weight: string;
  cpu_weight: string;
  ram_usage: string;
  privileged: boolean;
  permissions: Permission[];
  voter_info: VoterInfo;
}
export interface Wait {
  wait_sec: number;
  weight: number;
}
export interface KeyAuthority {
  key: string;
  weight: number;
}
export interface AccountAuthority {
  actor: string;
  permission: string;
}
export interface RequiredAuthority {
  waits: Wait[];
  keys: KeyAuthority[];
  threshold: number;
  accounts: AccountAuthority[];
}

export interface Permission {
  parent: string;
  perm_name: string;
  required_auth: RequiredAuthority;
}

export interface VoterInfo {
  owner: string;
  proxy: string;
  producers: string[];
  staked: string;
  last_vote_weight: string;
  proxied_vote_weight: string;
  is_proxy: number;
  flags1: number;
  reserved2: number;
  reserved3: string;
}

export interface ResourceDelegation {
  from: string;
  to: string;
  net_weight: string;
  cpu_weight: string;
}

export interface RefundRequest {
  owner: string;
  request_time: string;
  net_amount: string;
  cpu_amount: string;
}

export interface ResourceLimit {
  max: string;
  available: string;
  used: string;
}

export interface ResourceOverview {
  owner: string;
  ram_bytes: string;
  net_weight: string;
  cpu_weight: string;
}

export interface AtomicResponse<T> {
  success: boolean;
  query_time: number;
  data: T;
}

export interface AtomicTemplate {
  collection: any;
  contract: string;
  created_at_block: string;
  created_at_time: string;
  immutable_data: ImmutableData;
  name: string;
}
export interface ImmutableData {
  img: string;
  name: string;
  description: string;
}
export interface RecipeRow {
  recipe_id: number;
  recipe_display: string;
  start_ms: number;
  end_ms: number;
  burn: number;
  requirements: any[];
  result: string[];
}
export interface AtomicInventoryRow {
  asset_id: string;
  backed_tokens: any[];
  burned_at_block: null;
  burned_at_time: null;
  burned_by_account: null;
  collection: any;
  collection_mint: string;
  contract: string;
  data: any;
  immutable_data: any;
  is_burnable: true;
  is_transferable: true;
  minted_at_block: string;
  minted_at_time: string;
  mutable_data: any;
  name: string;
  owner: string;
  schema: any;
  schema_mint: string;
  template: any;
  template_mint: string;
  transferred_at_block: string;
  transferred_at_time: string;
  updated_at_block: string;
  updated_at_time: string;
}
export enum LoginMethod {
  Scatter,
  CloudWallet,
  Anchor,
}
const scatterNetwork = {
  blockchain: 'eos',
  protocol: 'https',
  host: 'wax.greymass.com',
  port: 443,
  chainId: '1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4'
};
const anchorNetwork = {
  chainId: '1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4',
  nodeUrl: 'https://wax.greymass.com',
};
const waxNodes = [
  'https://wax.greymass.com',
  'https://chain.wax.io',
  'https://wax.pink.gg',
  'https://api.waxsweden.org'
];
@Injectable({
  providedIn: 'root'
})
export class WaxService {
  wax: waxjs.WaxJS;
  preferredNode = 'https://wax.greymass.com';
  loginMethod: LoginMethod = null;
  rpc;
  scatter;
  scatterApi;
  anchorLink;
  anchorSession;
  userAccount: string;
  userAccountDetails$: Observable<WaxAccount>;
  atomicCache: Record<string, ImmutableData> = {};
  constructor(public httpClient: HttpClient) {
    this.testNodes().subscribe(result => {
      console.log('Fastest node foud : ', result);
      this.wax = new waxjs.WaxJS(result, null, null, false);
      this.rpc = new JsonRpc(result);
      ScatterJS.plugins(new ScatterEOS());
      (window as any).ScatterJS = null;
      const transport = new AnchorLinkBrowserTransport();
      this.anchorLink = new AnchorLink({
        transport,
        chains: [
          anchorNetwork
        ],
      });
    });
    this.userAccountDetails$ = new Observable<WaxAccount>(obs => {
      const checkLoggedIn = () => {
        if (!this.userAccount) {
          setTimeout(checkLoggedIn, 100);
          return;
        }
        this.getAccount(this.userAccount).subscribe(accountInfo => {
          obs.next(accountInfo);
        }, err => obs.error(err));
      };
      checkLoggedIn();
    });
  }

  testNodes(): Observable<string> {
    const observables$ = waxNodes.map((node: string) => {
      return from([0]).pipe(flatMap(() => {
        const start = Date.now();
        return this.httpClient.get(node + '/v1/chain/get_info').pipe(map(() => {
          return Date.now() - start;
        }));
      }));
    });
    return concat(...observables$).pipe(
      toArray(),
      map((result: number[]) => {
        result.forEach((value, index) => {
          console.log(waxNodes[index] + ' replied in ' + value + ' milliseconds');
        });
        const fastestNodeIndex = result.findIndex(value => value === Math.min(...result));
        scatterNetwork.host = waxNodes[fastestNodeIndex].replace('https://', '');
        anchorNetwork.nodeUrl = waxNodes[fastestNodeIndex];
        this.preferredNode = waxNodes[fastestNodeIndex];
        return waxNodes[fastestNodeIndex];
      })
    );
  }

  waxLogin(): Observable<string> {
    return new Observable(observer => {
      this.wax.isAutoLoginAvailable().then((isLoggedIn) => {
        if (!isLoggedIn) {
          this.wax.login().then((result: string) => {
            this.userAccount = result;
            this.loginMethod = LoginMethod.CloudWallet;
            observer.next(this.userAccount);
            observer.complete();
          }, (error) => {
            observer.error(error);
          });
        } else {
          // @ts-ignore
          this.userAccount = this.wax.userAccount;
          this.loginMethod = LoginMethod.CloudWallet;
          observer.next(this.userAccount);
          observer.complete();
        }
      }, (error) => {
        observer.error(error);
      });
    });
  }

  scatterLogin(): Observable<string> {
    return new Observable(observer => {
      ScatterJS.scatter.connect('Auto Unbox').then(connected => {
        if (!connected) {
          return console.log('Failed to connect with Scatter!');
        }
        this.scatter = ScatterJS.scatter;
        this.loginMethod = LoginMethod.Scatter;
        this.scatter.getIdentity({ accounts: [scatterNetwork] }).then(() => {
          this.userAccount = this.scatter.identity.accounts.find(
            e => e.blockchain === 'wax'
          );
          console.log(this.userAccount);
          this.scatterApi = this.scatter.eos(scatterNetwork, Api, { rpc: this.rpc });
          if (this.userAccount === null) {
            observer.error();
          }
          observer.next(this.userAccount);
          observer.complete();
        });
      });
    });
  }

  anchorLogin(): Observable<string> {
    return new Observable(observer => {
      this.anchorLink.login('Auto Unbox').then(({session}) => {
        this.anchorSession = session;
        this.userAccount = session.auth.actor.toString();
        this.loginMethod = LoginMethod.Anchor;
        observer.next(this.userAccount);
        observer.complete();
      });
    });
  }

  silentLogin(): Observable<string | boolean> {
    return new Observable(observer => {
      this.wax.isAutoLoginAvailable().then((isLoggedIn) => {
        if (!isLoggedIn) {
          observer.next(false);
        } else {
          // @ts-ignore
          this.userAccount = this.wax.userAccount;
          observer.next(this.userAccount);
        }
      }, (error) => {
        observer.error(error);
      });
    });
  }

  transact(actions: any[]): Observable<any> {
    console.log(actions, this.loginMethod);
    return new Observable<any>(observer => {
      switch (this.loginMethod) {
        case LoginMethod.CloudWallet:
          this.wax.api.transact({actions}, {
            blocksBehind: 3,
            expireSeconds: 1200,
          }).then(result => {
            observer.next(result);
            observer.complete();
          }).catch(error => {
            observer.error(error);
          });
          break;
        case LoginMethod.Scatter:
          this.scatterApi.transact(
            {
              actions,
            },
            {
              blocksBehind: 3,
              expireSeconds: 30
            }
          ).then(result => {
            observer.next(result);
            observer.complete();
          }).catch(error => {
            observer.error(error);
          });
          break;
        case LoginMethod.Anchor:
          this.anchorSession.transact({actions}).then(({signer, transaction}) => {
            observer.next(transaction);
            observer.complete();
            console.log(
              `Success! Transaction signed by ${signer} and bradcast with transaction id: ${transaction.id}`
            );
          }).catch(error => {
            observer.error(error);
          });
      }
    });
  }

  loadAtomicInventory(): Observable<AtomicInventoryRow[]> {
    return this.httpClient.get<AtomicResponse<AtomicInventoryRow[]>>(`https://wax.api.atomicassets.io/atomicassets/v1/assets?page=1&limit=1000&collection_name=stf.capcom&owner=${this.userAccount}`).pipe(
      map((result: AtomicResponse<AtomicInventoryRow[]>) => {
        return result.data;
      })
    );
  }

  loadPendingClaim(assocId: string, contract: 'unbox.nft' | 'craft.nft'): Observable<TableRowPending> {
    const body = {
      json: true,
      code: contract,
      scope: 'stf.capcom',
      table: 'pending',
      lower_bound: assocId,
      upper_bound: assocId,
      limit: 1,
    };
    const query$ = this.httpClient.post<TableRowsResult<TableRowPending>>(`${this.preferredNode}/v1/chain/get_table_rows`, body);
    return query$.pipe(
      expand(result => result.rows.length && result.rows[0].status == 1 ? EMPTY : query$),
      filter(result => {
        return result.rows.length && result.rows[0].status == 1;
      }),
      map(result => {
        return result.rows[0];
      })
    );
  }

  loadRecipes(): Observable<RecipeRow[]> {
    const body = {
      json: true,
      code: 'craft.nft',
      scope: 'stf.capcom',
      table: 'recipes',
      lower_bound: '',
      upper_bound: '',
      limit: 1000,
    };
    const query$ = this.httpClient.post<TableRowsResult<RecipeRow>>(`${this.preferredNode}/v1/chain/get_table_rows`, body);
    return query$.pipe(
      map(result => {
        return result.rows;
      })
    );
  }

  loadDrops(): Observable<TableRowDrop[]> {
    let nbResults = 0;
    const body = {
      json: true,
      code: 'atomicdropsx',
      scope: 'atomicdropsx',
      table: 'drops',
      table_key: '',
      lower_bound: '',
      upper_bound: '',
      index_position: 1,
      key_type: '',
      limit: 100,
      reverse: false,
      how_payer: false
    };
    return this.httpClient.post('https://chain.wax.io/v1/chain/get_table_rows', body).pipe(
      expand((result: TableRowsResult<TableRowDrop>) => {
        nbResults += result.rows.length;
        if (result.more && nbResults < Infinity) {
          body.lower_bound = result.rows[result.rows.length - 1].drop_id.toString();
          return this.httpClient.post('https://chain.wax.io/v1/chain/get_table_rows', body);
        } else {
          return EMPTY;
        }
      }),
      reduce((acc, value: TableRowsResult<TableRowDrop>) => {
        return acc.concat(value.rows.filter((row) => {
          const now = (new Date().getTime() / 1000);
          return (row.max_claimable === 0 || row.current_claimed < row.max_claimable) && (row.end_time === 0 || row.end_time > now);
        }));
      }, [])
    );
  }

  getWaxPrice(): Observable<number> {
    const body = {
      json: true,
      code: 'delphioracle',
      scope: 'waxpusd',
      table: 'datapoints',
      limit: 1,
    };
    return this.httpClient.post<TableRowsResult<TableRowPrice>>('https://chain.wax.io/v1/chain/get_table_rows', body).pipe(
      map((value: TableRowsResult<TableRowPrice>) => {
        return value.rows[0].median / 10000;
      })
    );
  }

  searchAsset(collectionName: string, templateId: number): Observable<ImmutableData> {
    if (`${collectionName}${templateId}` in this.atomicCache) {
      return of(this.atomicCache[`${collectionName}${templateId}`]);
    }
    const body = {
      json: true,
      code: 'atomicassets',
      scope: collectionName,
      table: 'templates',
      table_key: '',
      lower_bound: templateId,
      upper_bound: templateId,
      index_position: 1,
      key_type: '',
      limit: 100,
      reverse: false,
      how_payer: false
    };
    return this.httpClient.post(`https://wax2.api.atomicassets.io/atomicassets/v1/templates?collection_name=${collectionName}&ids=${templateId}`, body).pipe(
      map((result: AtomicResponse<AtomicTemplate[]>) => {
        this.atomicCache[`${collectionName}${templateId}`] = result.data[0].immutable_data;
        return result.data[0].immutable_data;
      })
    );
  }

  getAccount(accountName): Observable<WaxAccount> {
    const body = {
      account_name: accountName
    };
    return this.httpClient.post<WaxAccount>(`${this.preferredNode}/v1/chain/get_account`, body);
  }

  searchAccount(accountName: string): Observable<string[]> {
    const upperBound = accountName.padEnd(12, 'z');
    if (accountName.endsWith('.')) {
      accountName = accountName.slice(0, -1);
    }
    const body = {
      code: 'eosio',
      limit: 10,
      table: 'userres',
      lower_bound: accountName,
      upper_bound: upperBound
    };
    return this.httpClient.post<TableRowsResult<TableRowUser>>('https://chain.wax.io/v1/chain/get_table_by_scope', body).pipe(
      map(result => {
        return result.rows.map(user => user.scope);
      })
    );
  }

  getEosdtBalance(accountName: string): Observable<string> {
    // weosdttokens
    const body = {
      code: 'weosdttokens',
      account: accountName,
      symbol: 'WEOSDT'
    };
    return this.httpClient.post<string[]>('https://chain.wax.io/v1/chain/get_currency_balance', body).pipe(map(result => {
      if (result.length === 1) {
        return result[0];
      } else {
        return '0 WEOSDT';
      }
    }));
  }

}
